import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Spinner } from "@/components/Spinner/Spinner";

interface CostWindowPickerProps {
	window: { from?: string; to?: string };
	/** What the server actually applied, which is not always what was asked for. */
	applied: { from: string; to: string };
	isFetching: boolean;
	onChange: (window: { from?: string; to?: string }) => void;
	onReset: () => void;
}

/**
 * The date range, and — more importantly — the range the figures are actually for.
 *
 * Both ends are optional. Leaving them empty is the normal case and means the server's
 * twelve-month default, which is why the applied window is shown back rather than assumed
 * to match the inputs.
 */
export const CostWindowPicker: FunctionComponent<CostWindowPickerProps> = ({
	window,
	applied,
	isFetching,
	onChange,
	onReset,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`mediaCosts.${key}`, options ?? {});

	return (
		<section className="rounded-card border border-border-soft bg-white p-5">
			<div className="flex flex-wrap items-end gap-4">
				<label className="block">
					<span className="mb-1.5 block text-xs font-bold uppercase text-subtle">
						{ts("window.from")}
					</span>
					<input
						className="rounded-card border border-border bg-white px-3 py-2 text-sm text-text"
						max={window.to}
						type="date"
						value={window.from ?? ""}
						onChange={(event) => onChange({ ...window, from: event.target.value || undefined })}
					/>
				</label>

				<label className="block">
					<span className="mb-1.5 block text-xs font-bold uppercase text-subtle">
						{ts("window.to")}
					</span>
					<input
						className="rounded-card border border-border bg-white px-3 py-2 text-sm text-text"
						min={window.from}
						type="date"
						value={window.to ?? ""}
						onChange={(event) => onChange({ ...window, to: event.target.value || undefined })}
					/>
				</label>

				<Button
					className="!w-auto"
					color={ButtonColor.ACTION_SECONDARY}
					size={ButtonSize.S}
					title={ts("window.reset")}
					type="button"
					onClick={onReset}
				/>

				{isFetching && <Spinner />}
			</div>

			<p className="mt-3 text-xs text-subtle">
				{applied.from
					? ts("window.applied", { from: applied.from, to: applied.to })
					: ts("window.defaultHint")}
			</p>
		</section>
	);
};
